<template>
  <div class="ar">
    <div class="ar-content">
      <div
        v-if="!audio"
        class="ar-recorder"
      >
        <span v-if="isLoading">Aguarde!</span>
        <template v-else>
          <icon-button
            v-if="!isRecording"
            class="ar-icon ar-icon__lg"
            name="mic"
            :class="{
              'ar-icon--rec': Boolean(isRecording),
              'ar-icon--pulse': isRecording && volume > 0.02
            }"
            data-testid="gravador-iniciar"
            @click.native="startRecordVoice"
          />

          <icon-button
            class="ar-icon ar-icon__sm ar-recorder__stop"
            name="stop"
            v-if="isRecording"
            data-testid="gravador-parar"
            @click.native="stopRecordVoice"
          />

          <div
            v-if="!isLoading && !audio && isRecording && recordedTime"
            class="ar-recorder__duration"
            style="margin-top: 7px"
          >
            {{ recordedTime }}
          </div>
        </template>
      </div>

      <AudioPlayer
        v-if="audio"
        :src="audio.url"
      />

      <div
        v-if="audio"
        class="row justify-around full-width items-center q-mt-md"
      >
        <q-btn
          round
          outline
          color="negative"
          icon="mdi-microphone-off"
          class="col-auto q-mx-sm"
          data-testid="gravador-descartar"
          @click="removeRecord"
        >
          <q-tooltip content-class="bg-negative">
            Descartar Áudio
          </q-tooltip>
        </q-btn>

        <q-btn
          round
          outline
          color="positive"
          icon="mdi-check-all"
          class="col-auto q-mx-sm"
          data-testid="gravador-enviar"
          @click="uploadAudio"
        >
          <q-tooltip content-class="bg-positive">
            Fazer upload do Áudio
          </q-tooltip>
        </q-btn>
      </div>
    </div>
  </div>
</template>

<script>
import AudioPlayer from './AudioPlayer'
import IconButton from './icon-button'
import { convertTimeMMSS } from './utils'
import { mapGetters } from 'vuex'

export default {
  components: {
    AudioPlayer,
    IconButton
  },

  props: {
    value: {}
  },

  data () {
    return {
      audio: null,
      isLoading: false,
      isRecording: null,
      recordedTime: '00:00'
    }
  },

  mounted () {
    const vm = this
    vm.forceStop = false
    vm.recorder = null
  },

  beforeDestroy () {
    this.forceStop = true
    this.stopRecordVoice()
  },

  computed: {
    ...mapGetters({
      customerId: 'settingsModule/customer'
    })
  },

  methods: {
    clearTimeout () {
      const vm = this
      if (vm.timeSecs) clearInterval(vm.timeSecs)
      vm.timeSecs = null
    },

    startRecordVoice () {
      const vm = this
      vm.clearTimeout()
      vm.removeRecord()
      vm.isLoading = true

      let chunks = []

      getStremFromDevice().then((stream) => {
        // eslint-disable-next-line no-undef
        vm.recorder = new Mp3MediaRecorder(stream, { worker: new Worker('/lib/mp3-background.js') })

        vm.recorder.ondataavailable = (e) => {
          chunks.push(e.data)
        }

        vm.recorder.onerror = (e) => {
          console.log('onerror', e)
          vm.isLoading = false
          vm.clearTimeout()
          vm.removeRecord()
        }

        vm.recorder.onstop = (e) => {
          console.log('onstop', e)
          vm.isLoading = false
          vm.clearTimeout()
          if (!chunks.length) return

          if (!vm.forceStop) {
            const blob = new File(chunks, `vonixphone_${Date.now()}.mp3`, { type: 'audio/mpeg' })
            blob.codec = 'audio/mpeg'
            const url = URL.createObjectURL(blob)
            vm.audio = { url, blob }
          }

          chunks = []
        }

        vm.recorder.onstart = (e) => {
          console.log('onstart', e)
          vm.isLoading = false
          vm.isRecording = Date.now()

          vm.timeSecs = setInterval(() => {
            vm.recordedTime = convertTimeMMSS(Math.floor((Date.now() - vm.isRecording) / 1000))
          }, 1000)
        }

        vm.recorder.start()
      }).catch((err) => {
        console.log(err)
      })
    },

    async stopRecordVoice () {
      const vm = this
      vm.clearTimeout()
      if (vm?.recorder?.state === 'inactive') return
      vm.recorder?.stop()
      vm.recorder?.stream.getTracks().forEach(i => i.stop())
    },

    uploadAudio () {
      const vm = this
      vm.$emit('input', vm.audio.blob)
      vm.removeRecord()
    },

    removeRecord () {
      const vm = this

      if (vm.audio) {
        URL.revokeObjectURL(vm.audio.url)
        vm.audio.blob = null
      }

      vm.audio = null
      vm.isRecording = null
    }
  }
}

const getStremFromDevice = () => (
  navigator.mediaDevices.getUserMedia({
    audio: true
  }).catch(() => null)
)

</script>

<style lang="scss">
  .ar {
    border-radius: 16px;
    background-color: #FAFAFA;
    box-shadow: 0 4px 18px 0 rgba(0,0,0,0.17);
    position: relative;
    box-sizing: content-box;

    &-content {
      padding: 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    &-spinner {
      display: flex;
      height: 30px;
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      margin: auto;
      width: 144px;
      z-index: 10;

      &__dot {
        display: block;
        margin: 0 8px;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        background: #05CBCD;
        animation-name: blink;
        animation-duration: 1.4s;
        animation-iteration-count: infinite;
        animation-fill-mode: both;

        &:nth-child(2) { animation-delay: .2s; }

        &:nth-child(3) { animation-delay: .4s; }

        @keyframes blink {
          0%    { opacity: .2; }
          20%   { opacity: 1;  }
          100%  { opacity: .2; }
        }
      }
    }

    &__text {
      color: rgba(84,84,84,0.5);
      font-size: 16px;
    }

    &__blur {
      filter: blur(2px);
      opacity: 0.7;
    }

    &__overlay {
      position: absolute;
      width: 100%;
      height: 100%;
      z-index: 10;
    }

    &__rm {
      cursor: pointer;
      position: absolute;
      width: 6px;
      height: 6px;
      padding: 6px;
      line-height: 6px;
      margin: auto;
      left: 10px;
      bottom: 0;
      top: 0;
      color: rgb(244, 120, 90);
    }
  }

  @import './icons';
</style>
